import type { ReactNode, Ref } from "react";
import { Knopf } from "./knopf";
import { SymbolHerunterladen, SymbolLaedt, SymbolPapierkorb } from "./zeilen-symbole";

/**
 * Dateizeile (Musterkatalog M-011 Teil 3, Design-Guide `datei-liste.md`): Typ-Symbol, Name als Download-Link, Meta in
 * der zweiten Zeile („1,2 MB · 07.10.2026 · Anna Becker“, bei 375 px kürzt nur die Person), rechts Herunterladen und Löschen. Beide Aktionen sind IMMER
 * sichtbar (Tastatur, Touch; der Block `profile-files-list` zeigt sie nur beim Darüberfahren). Ohne `onLoeschen` (Beobachter)
 * bleibt nur Herunterladen. Zustand `laedt` = die Datei wird gerade hochgeladen: Lade-Symbol, Name ohne Link, „wird
 * hochgeladen …“ statt Meta, keine Aktionen (kein Prozentwert, Entscheid E2).
 *
 * Der Baustein prüft keine Rechte und lädt nichts: Der Aufrufer übergibt `onLoeschen` nur mit Schreibrecht, die Action
 * prüft selbst (r109). Die Rückfrage vor dem Löschen kommt über den Platz `unten` (Rückfrage in der Zeile, wie 4A).
 */
const SYMBOL_KASTEN =
  "shrink-0 inline-flex size-8 items-center justify-center rounded-lg border border-layer-line bg-layer text-muted-foreground-1";
// Herunterladen bleibt ein eigener `<a download>` (Knopf kennt kein `download`, keine freien Attribute): Maße und Farben
// wie der Ghost-Symbolknopf `sm` (34 px, rounded-lg), Fokus = Hintergrund (Betreiber 10.10.2026, kein Ring).
const AKTION =
  "size-8.5 shrink-0 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden focus:bg-muted-focus";

export interface DateiMeta {
  /** „1,2 MB“. */
  groesse: string;
  /** „07.10.2026“, nie gekürzt. */
  datum: string;
  /** Person; fehlt sie (unbekannt), entfällt sie samt Trenner. */
  person?: string | null;
}

export interface DateiZeileProps {
  /** Dateiname, wie gespeichert (Inhalt, nie im Log). */
  name: string;
  /** Adresse der Download-Route; fehlt sie (Zustand `laedt`), ist der Name kein Link. */
  href?: string;
  /** Meta in der Reihenfolge Größe · Datum · Person; die Person ist der einzige kürzbare Teil (375 px). */
  meta?: DateiMeta;
  /** Typ-Symbol (Lucide, vom Aufrufer nach Dateiart gewählt). */
  symbol: ReactNode;
  /** Datei wird gerade hochgeladen. */
  laedt?: boolean;
  /** Nur mit Schreibrecht übergeben; ohne bleibt Herunterladen. */
  onLoeschen?: () => void;
  /** Aktion läuft (Löschen), Knöpfe gesperrt. */
  belegt?: boolean;
  /** Ref auf den Löschknopf (Fokus-Rückkehr nach Abbrechen). */
  loeschenRef?: Ref<HTMLButtonElement>;
  /** Satz an der Zeile (Fehlschlag/Ablehnung der Zeile). */
  fehler?: ReactNode;
  /** Bereich unter der Zeile: Rückfrage vor dem Löschen. */
  unten?: ReactNode;
}

export function DateiZeile({ name, href, meta, symbol, laedt = false, onLoeschen, belegt = false, loeschenRef, fehler, unten }: DateiZeileProps) {
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-file-views/profile-files-list */}
      {/* Abweichung AW-014: flache Zeile mit Haarlinie (Vorschau A2) statt Karte je Datei; Name als Link font-medium text-primary; Symbol im Rahmen size-8; Download-Knopf zusätzlich; Herunterladen/Löschen immer sichtbar statt hover-reveal; natives title statt Tooltip-Baustein */}
      <li className="py-2.5 first:pt-0 border-t border-line-2 first:border-t-0" aria-busy={laedt || belegt || undefined}>
        <div className="flex items-center gap-x-3">
          <span className={SYMBOL_KASTEN}>{laedt ? <SymbolLaedt /> : symbol}</span>
          <div className="min-w-0 grow">
            {laedt || !href ? (
              <p className="truncate text-sm font-medium text-foreground">{name}</p>
            ) : (
              <a
                className="block truncate text-sm font-medium text-primary hover:text-primary-hover hover:underline focus:outline-hidden focus-visible:underline"
                href={href}
                download
              >
                {name}
              </a>
            )}
            {laedt ? (
              <p className="truncate text-xs text-muted-foreground-1" role="status">
                wird hochgeladen …
              </p>
            ) : (
              meta && (
                <p className="flex min-w-0 items-baseline text-xs text-muted-foreground-1">
                  <span className="shrink-0">{meta.groesse}</span>
                  <span className="shrink-0 whitespace-pre"> · {meta.datum}</span>
                  {meta.person && (
                    <>
                      <span className="shrink-0 whitespace-pre"> · </span>
                      <span className="min-w-0 truncate">{meta.person}</span>
                    </>
                  )}
                </p>
              )
            )}
          </div>
          {!laedt && href && (
            <div className="shrink-0 flex items-center gap-x-1">
              <a className={AKTION} href={href} download aria-label={`Herunterladen: ${name}`} title="Herunterladen">
                <SymbolHerunterladen />
              </a>
              {onLoeschen && (
                <Knopf
                  ref={loeschenRef}
                  variante="ghost"
                  nurSymbol
                  groesse="sm"
                  starr
                  disabled={belegt}
                  onClick={onLoeschen}
                  label={`Anhang löschen: ${name}`}
                  titel="Löschen"
                  symbol={<SymbolPapierkorb />}
                />
              )}
            </div>
          )}
        </div>
        {fehler}
        {unten}
      </li>
    </>
  );
}
